import { useQueryClient } from '@tanstack/react-query';
import { deleteTeam, editTeam } from 'api/teams';
import { teamsKeys } from 'api/queryKeys';
import { Box, Flex, Line, Modal, UserAvatarRow } from 'components';
import { ImagesPicker } from 'components/ImagesPicker/ImagesPicker';
import { H1, P } from 'components/Typography/Typography';
import {
  Button,
  Select,
  SwitchButton,
  TextareaField,
  TextField,
  UserSelect
} from 'components/_form';
import { t } from 'i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { getFile } from 'utilities/files';
import { Wrapper } from '../../CompanyAdministrationView/CompanyAdministrationView.styled';
import { BackButton } from 'components/BackButton';
import { NestedSelect } from 'components/_form/NestedSelect/NestedSelect';
import {
  expandSelectedAreaIdsWithAncestors,
  findOptionByValueInTree
} from 'components/_form/NestedSelect/utilities';
import { IOption } from 'components/_form/Select/Select';
import { useAreasQuery } from 'hooks/queries/useAreasQuery';
import { useMainCompanyQuery } from 'hooks/queries/useMainCompanyQuery';
import { useTeamQuery } from 'hooks/queries/useTeamQuery';
import { useTeamsQuery } from 'hooks/queries/useTeamsQuery';
import { useUserFunctionsQuery } from 'hooks/queries/useUserFunctionsQuery';
import { useUsersQuery } from 'hooks/queries/useUsersQuery';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPeopleGroup, faPlusCircle, faRotate, faTrashCan, faUserSlash } from '@fortawesome/free-solid-svg-icons';
import { ImageBox, StyledTable } from './TeamDetailsView.styled';
import { ITeamForm } from 'types/forms/team';
import { ITeam } from 'types/team';
import { toast } from 'react-toastify';
import { getUserDisplayLabel } from 'utilities/fullName';
import {
  IUserSelectOption,
  toUserSelectOption
} from 'utilities/userSelectOption';
import {
  parentToChildrenAreasStructure,
  parseParentToChildrenAreasStructureToOptions
} from 'utilities/areas';
import {
  buildMembersTeamsAttributes,
  getTeamMembers
} from 'utilities/teamMembers';
import {
  filterUserFunctionsByAreaIds,
  mapUserFunctionsToOptions
} from 'utilities/userFunctions';

interface IMemberOption extends IUserSelectOption {
  role?: string;
  members_team_id?: string;
  function_id?: string;
}

const toMemberOption = (member: {
  id: string | number;
  first_name?: string;
  last_name?: string;
  email?: string;
  username?: string;
  role?: string;
  photo?: string;
  function_id?: string;
  members_team_id?: string | number;
  profile?: { first_name?: string; last_name?: string; photo?: string };
}): IMemberOption => ({
  ...toUserSelectOption(member),
  role: member.role,
  members_team_id:
    member.members_team_id != null && member.members_team_id !== ''
      ? String(member.members_team_id)
      : undefined,
  function_id:
    member.function_id != null && member.function_id !== ''
      ? String(member.function_id)
      : undefined
});

interface ITeamsOption extends IOption {
  members: IMemberOption[];
}

interface Props {}

export const TeamDetailsView: React.FC<Props> = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { data: mainCompany } = useMainCompanyQuery();
  const companyId = mainCompany?.id ?? '';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: usersData = [] } = useUsersQuery();
  const { data: userFunctionsData = [] } = useUserFunctionsQuery();
  const users = useMemo<IMemberOption[]>(
    () =>
      usersData.map((user) =>
        toMemberOption({
          id: user.id,
          email: user.email,
          username: user.username,
          role: user.role,
          profile: user.profile
        })
      ),
    [usersData]
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { data: teamsData = [] } = useTeamsQuery();
  const teams = useMemo<ITeamsOption[]>(
    () =>
      teamsData.map((team) => ({
        value: String(team.id),
        label: team.name,
        members: getTeamMembers(team).map((member) => toMemberOption(member))
      })),
    [teamsData]
  );
  const { data: teamData } = useTeamQuery(id ?? null);
  const { data: areasData = [] } = useAreasQuery();
  const areaOptionsTree = useMemo(
    () =>
      parseParentToChildrenAreasStructureToOptions(
        parentToChildrenAreasStructure(areasData)
      ),
    [areasData]
  );
  const [availableUsers, setAvailableUsers] = useState<IMemberOption[]>([]);

  const { register, control, reset, watch, setValue, handleSubmit, getValues } =
    useForm<ITeamForm['team']>();

  const navigate = useNavigate();

  const goToTeamsList = () =>
    navigate('/app/administration/employees-and-users?tab=teams');

  const ref = useRef<HTMLInputElement>(null);
  const initializedTeamIdRef = useRef<string | null>(null);

  useEffect(() => {
    initializedTeamIdRef.current = null;
  }, [id]);

  useEffect(() => {
    if (!id || !teamData) return;
    if (initializedTeamIdRef.current === id) return;

    const data = teamData;
    let cancelled = false;

    const {
      id: _id,
      image_urls,
      logo_url,
      leader,
      manager,
      areas: areasList,
      members_teams: _membersTeams,
      ...restData
    } = data;

    (async () => {
      reset({
        ...restData,
        images: image_urls?.length
          ? (await Promise.all(
              image_urls.map((image_url: string) => getFile(image_url))
            )) ?? []
          : [],
        logo: logo_url ? await getFile(logo_url) : undefined,
        leader: {
          value: leader.id,
          label: getUserDisplayLabel({
            first_name: (leader as any).first_name,
            last_name: (leader as any).last_name,
            email: leader.email,
            username: leader.username
          })
        },
        manager: {
          value: manager.id,
          label: getUserDisplayLabel({
            first_name: (manager as any).first_name,
            last_name: (manager as any).last_name,
            email: manager.email,
            username: manager.username
          })
        },
        areas: areasList
          .map(({ id: areaId }: { id: number }) =>
            findOptionByValueInTree(areaOptionsTree, String(areaId))
          )
          .filter((o): o is IOption => !!o),
        members: getTeamMembers(data).map((member) => toMemberOption(member))
      });

      if (!cancelled) {
        initializedTeamIdRef.current = id;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, teamData, areaOptionsTree, reset]);

  useEffect(() => {
    if (!teamData || !areaOptionsTree.length || getValues('areas')?.length) {
      return;
    }

    const areas = teamData.areas
      .map(({ id: areaId }: { id: number }) =>
        findOptionByValueInTree(areaOptionsTree, String(areaId))
      )
      .filter((o): o is IOption => !!o);

    if (areas.length === 0) return;

    setValue('areas', areas, { shouldDirty: false });
  }, [teamData, areaOptionsTree, setValue, getValues]);

  const onSubmit = async (data: ITeamForm['team']) => {
    if (!id) return;

    setIsSubmitting(true);
    try {
      const { leader, manager, areas: selectedAreas, logo, members, ...restData } =
        data;

      const payload = {
        team: {
          ...restData,
          leader_id: leader?.value,
          manager_id: manager?.value,
          area_ids: expandSelectedAreaIdsWithAncestors(
            areaOptionsTree,
            selectedAreas ?? []
          ),
          members_teams_attributes: buildMembersTeamsAttributes(
            teamData?.members_teams,
            members
          ),
          logo: logo || null
        }
      };

      const { data: updatedTeam } = await editTeam(id, payload);
      queryClient.setQueryData(teamsKeys.detail(companyId, id), updatedTeam);
      queryClient.setQueryData<ITeam[]>(teamsKeys.list(companyId), (old = []) =>
        old.map((t) => (String(t.id) === id ? updatedTeam : t))
      );

      toast.success(t('common.success'));
    } catch (e) {
      toast.error(t('common.anErrorOccurred'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const members = watch('members');
  const logo = watch('logo');
  const selectedAreas = watch('areas') ?? [];

  const teamContextAreaIds = useMemo(
    () =>
      expandSelectedAreaIdsWithAncestors(areaOptionsTree, selectedAreas),
    [areaOptionsTree, selectedAreas]
  );

  const userFunctionsList = useMemo(
    () =>
      mapUserFunctionsToOptions(
        filterUserFunctionsByAreaIds(userFunctionsData, teamContextAreaIds)
      ),
    [userFunctionsData, teamContextAreaIds]
  );

  const logoPreviewUrl = useMemo(() => {
    if (!logo) return undefined;
    return URL.createObjectURL(logo);
  }, [logo]);

  useEffect(() => {
    return () => {
      if (logoPreviewUrl) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
    };
  }, [logoPreviewUrl]);

  useEffect(() => {
    setAvailableUsers(
      users.filter(
        (user) =>
          !members?.some(
            (member) => String(member.value) === String(user.value)
          )
      )
    );
  }, [users, members]);

  const appendMembers = (incoming: IMemberOption[]) => {
    if (!incoming.length) return;

    const byId = new Map<string, IMemberOption>();

    [...(members ?? []), ...incoming].forEach((member) => {
      const key = String(member.value);
      const existing = byId.get(key);

      if (!existing) {
        byId.set(key, member);
        return;
      }

      if (!existing.function_id && member.function_id) {
        byId.set(key, { ...existing, function_id: member.function_id });
      }
    });

    setValue('members', Array.from(byId.values()));
  };

  const updateMemberFunction = (memberId: string, functionId: string) => {
    setValue(
      'members',
      (members ?? []).map((member) =>
        String(member.value) === String(memberId)
          ? { ...member, function_id: functionId }
          : member
      )
    );
  };

  return (
    <Wrapper flexDirection="column">
      <Flex width="100%">
        <BackButton customOnClick={goToTeamsList} />
        <H1 m={2} variant={['h3', 'h2']} textTransform="uppercase">
          {t('administration.administrationLabel')} -{' '}
          {t('administration.employeesAndUsers')} |{' '}
          {t('employeesAndUsersView.form.edit')} - {watch('name') || ''}
        </H1>
      </Flex>
      <Line />
      <Flex minHeight="400px">
        <Flex flexWrap="wrap" width="100%">
          <Box width={['100%', '50%']}>
            <ImagesPicker
              images={watch('images') || []}
              setImages={(images: File[]) => {
                setValue('images', images);
              }}
              allowEdit
            />
          </Box>
          <Flex
            width={['100%', '50%']}
            gap="10px"
            flexDirection="column"
            pl={4}
          >
            <Flex justifyContent="space-between" alignItems="center">
              <Flex width="65%" flexDirection="column">
                <Flex
                  width="100%"
                  justifyContent="space-between"
                  mb={2}
                  alignItems="end"
                >
                  <Flex flexDirection="column">
                    <P mb={1} variant="body">
                      {t('administration.active?')}
                    </P>
                    <SwitchButton
                      value={watch('state') === 'active'}
                      onChange={(value) => {
                        setValue('state', value ? 'active' : 'inactive');
                      }}
                    />
                  </Flex>

                  <Flex gap="10px" alignItems="end">
                    <TextField
                      {...register('id_code')}
                      label="administration.areaCode"
                    />
                    <Button
                      variant="red"
                      icon={<FontAwesomeIcon icon={faPeopleGroup} size="xl" />}
                      onClick={() => {
                        setIsDeleteModalOpen(true);
                      }}
                    />
                  </Flex>

                  <Flex gap="10px" alignItems="end">
                    {logo && (
                      <Button
                        variant="red"
                        icon={<FontAwesomeIcon icon={faTrashCan} />}
                        onClick={() => {
                          setValue('logo', undefined);
                          if (ref.current) ref.current.value = '';
                        }}
                      />
                    )}
                    <Button
                      variant="eucalyptus"
                      onClick={() => {
                        ref?.current?.click();
                      }}
                    >
                      <FontAwesomeIcon icon={logo ? faRotate : faPlusCircle} />{' '}
                      {t('employeesAndUsersView.tableHeaders.logo')}
                    </Button>
                  </Flex>
                </Flex>
                <TextField {...register('name')} label="profile.name" />
              </Flex>
              <Flex width="30%">
                <input
                  type="file"
                  ref={ref}
                  hidden
                  onChange={(e) => {
                    setValue('logo', e?.target?.files && e?.target?.files[0]);
                  }}
                />
                <ImageBox imageUrl={logoPreviewUrl} flexShrink="0">
                  {!logo && t('employeesAndUsersView.form.photoPlaceholder')}
                </ImageBox>
              </Flex>
            </Flex>
            <TextareaField
              {...register('description')}
              label={t('administration.description') as string}
              rows={4}
            />

            <Flex flexWrap={['wrap', 'nowrap']} alignItems="flex-end">
              <Flex flexDirection="column" width="100%">
                <Flex flexWrap={['wrap', 'nowrap']} alignItems="flex-end">
                  <Controller
                    control={control}
                    name="areas"
                    render={({ field: { onChange, value } }) => (
                      <NestedSelect
                        label={t('employeesAndUsersView.form.areas') as string}
                        selectedOptions={value ?? []}
                        setSelectedOptions={onChange}
                        options={areaOptionsTree}
                      />
                    )}
                  />
                </Flex>
                <Flex
                  gap="10px"
                  flexWrap={['wrap', 'nowrap']}
                  alignItems="flex-end"
                >
                  <Controller
                    control={control}
                    name="leader"
                    render={({ field: { onChange, value } }) => (
                      <UserSelect
                        label={t('employeesAndUsersView.form.teamLeader')}
                        selectedOptions={value}
                        onChange={(val) => {
                          onChange(val);
                        }}
                        options={users}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="manager"
                    render={({ field: { onChange, value } }) => (
                      <UserSelect
                        label={t('employeesAndUsersView.form.teamManager')}
                        selectedOptions={value}
                        onChange={(val) => {
                          onChange(val);
                        }}
                        options={users}
                      />
                    )}
                  />
                </Flex>
              </Flex>
            </Flex>

            <Flex gap="10px">
              <UserSelect
                isMulti
                hideElements
                closeMenuOnSelect={false}
                label="tasks.addTeamMember"
                placeholder={t('buttons.choosePlaceholder')}
                onChange={(val) => {
                  appendMembers(
                    (Array.isArray(val) ? val : val ? [val] : []) as IMemberOption[]
                  );
                }}
                selectedOptions={[]}
                options={availableUsers}
              />
              <Select
                isMulti
                hideElements
                closeMenuOnSelect={false}
                label="tasks.addFromTeamOrGroup"
                placeholder={t('buttons.choosePlaceholder')}
                onChange={(val) => {
                  const selectedTeams = (
                    Array.isArray(val) ? val : val ? [val] : []
                  ) as ITeamsOption[];

                  appendMembers(selectedTeams.flatMap((team) => team.members));
                }}
                selectedOptions={[]}
                options={teams}
              />
            </Flex>

            <StyledTable>
              <thead>
                <th>
                  <P variant="body">{t('tasks.teamMembers')}</P>
                </th>
                <th>
                  <P variant="body">{t('tasks.role/function')}</P>
                </th>
                <th></th>
              </thead>
              <tbody>
                {members?.map((member: IMemberOption) => (
                  <tr key={member.value}>
                    <td>
                      <UserAvatarRow
                        photo={member.photo}
                        first_name={member.first_name}
                        last_name={member.last_name}
                        email={member.email}
                        username={member.username}
                      />
                    </td>
                    <td align="center">
                      <Flex mt={2}>
                        <Select
                          withoutMargin
                          placeholder={t('buttons.choosePlaceholder')}
                          selectedOptions={userFunctionsList.find(
                            (fn) =>
                              String(fn.value) === String(member.function_id)
                          )}
                          onChange={(val) => {
                            updateMemberFunction(
                              member.value,
                              (val as IOption).value
                            );
                          }}
                          options={userFunctionsList}
                        />
                      </Flex>
                    </td>
                    <td>
                      <Button
                        variant="red"
                        icon={<FontAwesomeIcon icon={faUserSlash} />}
                        onClick={() => {
                          setValue(
                            'members',
                            (members ?? []).filter(
                              (filterMember) =>
                                filterMember.value !== member.value
                            )
                          );
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </StyledTable>

            <Flex mt={3} justifyContent="flex-end">
              <Box mr={2}>
                <Button variant="greyWhite" onClick={goToTeamsList}>
                  {t('buttons.cancel')}
                </Button>
              </Box>
              <Button
                variant="eucalyptus"
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting}
              >
                {t('buttons.save')}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Flex>

      <Modal
        isOpen={isDeleteModalOpen}
        onCancelClick={() => {
          setIsDeleteModalOpen(false);
        }}
        headerTitle={t('employeesAndUsersView.form.areYouSureDeleteTeam')}
          mainButton={{
            action: async () => {
              if (id) {
                try {
                  await deleteTeam(id);
                  queryClient.setQueryData<ITeam[]>(
                    teamsKeys.list(companyId),
                    (old = []) => old.filter((t) => String(t.id) !== id)
                  );
                  queryClient.removeQueries({ queryKey: teamsKeys.detail(companyId, id) });
                  toast.success(t('common.success'));
                  goToTeamsList();
                } catch (e) {
                  toast.error(t('common.anErrorOccurred'));
                }
              }
            },
          variant: 'eucalyptus',
          label: t('buttons.delete')
        }}
        isSecondButtonVisible
      />
    </Wrapper>
  );
};
